import React, { useCallback, useMemo, useState } from 'react';

import {
  BdsGrid,
  BdsModal,
  BdsTypo,
  BdsTableBody,
  BdsTableHeader,
  BdsTableTh,
  BdsTableRow,
  BdsTableCell,
  BdsRadio,
  BdsIcon,
} from 'blip-ds/dist/blip-ds-react';
import { ModalContent, GhostText, DocumentNameContainer, TruncatedDocumentName, Table, RadioGroup } from './styles';
import { KnowledgeBaseApi } from '../../../../api/KnowledgeBaseApi';
import { showToast } from '../../../../api/CommonApi';
import { useContentLocalizer } from '../../../../hooks/useContentLocalizer';
import { useModalManagement } from '../../../../hooks/useModalManagement';
import { useKnowledgeBaseTracking } from '../../../../hooks/useKnowledgeBaseTracking';
import { ModalHeader } from '../../../../components/ModalHeader';
import { ModalFooter } from '../../../../components/ModalFooter';
import { localization } from './localization';
import { Document } from '../../../../models/knowledgeBase/Document';
import { useCommon } from '../../../../contexts/CommonContext';
import { formatDateTimeWithLocale } from 'utils/date';
import { getDocumentIconName, formatFileSize } from '../../../../utils/fileUtil';
import { KNOWLEDGE_CONTENT_RESTORE, KNOWLEDGE_CONTENT_CANCEL } from '../../../../shared/SegmentEvents';

interface RestoreFileModalProps {
  isOpen: boolean;
  tenantId: string;
  catalogId: string;
  document: Document | null;
  onClose: (wasCancelled: boolean) => void;
}

const RestoreFileModal: React.FC<RestoreFileModalProps> = ({ isOpen, tenantId, catalogId, document, onClose }) => {
  const [selectedVersionId, setSelectedVersionId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const translate = useContentLocalizer(localization);
  const { trackKnowledgeBaseEvent } = useKnowledgeBaseTracking();
  const {
    common: { language },
  } = useCommon();

  const versions = useMemo(() => {
    if (!document?.versions) return [];
    return document.versions;
  }, [document?.versions]);

  const currentVersionId = document?.version_id ?? '';

  const formatDateTime = useCallback(
    (dateString: string): string => {
      return formatDateTimeWithLocale(dateString, language).replace(',', ` ${translate.at}`);
    },
    [language, translate.at],
  );

  const truncateDocumentName = useCallback((name: string, extension: string, maxLength = 25): string => {
    // If the name is already short enough, return as is
    if (name.length <= maxLength) {
      return name;
    }

    // Find the last dot to separate name from extension
    const lastDotIndex = name.lastIndexOf('.');
    if (lastDotIndex === -1) {
      // No extension in the name, just truncate
      return `${name.substring(0, maxLength - 3)}...`;
    }

    const nameWithoutExt = name.substring(0, lastDotIndex);
    const fileExtension = name.substring(lastDotIndex);

    // Calculate how much space we have for the name part
    const extensionLength = fileExtension.length;
    const ellipsisLength = 3; // "..."
    const availableLength = maxLength - extensionLength - ellipsisLength;

    if (availableLength <= 0) {
      // Extension is too long, just show truncated extension
      return `...${fileExtension}`;
    }

    return `${nameWithoutExt.substring(0, availableLength)}...${fileExtension}`;
  }, []);

  const resetForm = useCallback(() => {
    setSelectedVersionId(currentVersionId);
    setIsLoading(false);
  }, [currentVersionId]);

  const handleTrackingCancel = useCallback(() => {
    if (isLoading) return;
    trackKnowledgeBaseEvent(KNOWLEDGE_CONTENT_CANCEL);
  }, [isLoading, trackKnowledgeBaseEvent]);

  const { modalRef, handleCancel } = useModalManagement({
    isOpen,
    onClose,
    resetForm,
    onCancel: handleTrackingCancel,
    shouldAllowCancel: !isLoading,
  });

  const handleVersionSelect = useCallback(
    (versionId: string) => {
      if (isLoading) {
        return;
      }
      setSelectedVersionId(versionId);
    },
    [isLoading],
  );

  const handleRestore = useCallback(async () => {
    if (!document || !selectedVersionId || isLoading || selectedVersionId === currentVersionId) {
      return;
    }

    const selectedVersion = versions.find((v) => v.version_id === selectedVersionId);
    if (!selectedVersion) {
      return;
    }

    try {
      setIsLoading(true);

      await KnowledgeBaseApi.updateDocumentAsync(tenantId, catalogId, document.id, { version_id: selectedVersionId });

      trackKnowledgeBaseEvent(KNOWLEDGE_CONTENT_RESTORE, {
        knowcontName: document.name,
        knowcontType: document.extension,
        knowcontVersion: selectedVersion.version_id,
      });

      showToast({
        title: translate.successToast.title,
        message: translate.successToast.message,
        type: 'success',
      });

      resetForm();
      onClose(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error restoring document version:', error);
      showToast({
        title: translate.errorToast.title,
        message: translate.errorToast.message,
        type: 'danger',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    document,
    selectedVersionId,
    tenantId,
    catalogId,
    onClose,
    resetForm,
    isLoading,
    currentVersionId,
    versions,
    translate,
    trackKnowledgeBaseEvent,
  ]);

  const isFormValid = !!(selectedVersionId && selectedVersionId !== currentVersionId);

  const renderVersionRow = useCallback(
    (version: Document) => (
      <BdsTableRow key={version.version_id}>
        <BdsTableCell>
          <BdsGrid direction="row" alignItems="center" gap="2">
            <BdsIcon theme="outline" name={getDocumentIconName(version.extension)} size="medium" />
            <DocumentNameContainer direction="column">
              <TruncatedDocumentName variant="fs-14" bold="bold" title={version.name}>
                {truncateDocumentName(version.name, version.extension)}
              </TruncatedDocumentName>
              <GhostText variant="fs-12">
                {version.extension === 'url' ? version.url : formatFileSize(version.content_size_bytes)}
              </GhostText>
            </DocumentNameContainer>
          </BdsGrid>
        </BdsTableCell>
        <BdsTableCell>
          <BdsGrid direction="column">
            <BdsTypo variant="fs-14">{formatDateTime(version.updated_at)}</BdsTypo>
            <GhostText variant="fs-12">{`${translate.by} ${version.updated_by}`}</GhostText>
          </BdsGrid>
        </BdsTableCell>
        <BdsTableCell>
          <BdsRadio
            name="version-select"
            value={version.version_id}
            checked={selectedVersionId === version.version_id}
            onClick={() => handleVersionSelect(version.version_id)}
            disabled={isLoading}
          />
        </BdsTableCell>
      </BdsTableRow>
    ),
    [selectedVersionId, handleVersionSelect, getDocumentIconName, formatDateTime, translate.by, truncateDocumentName],
  );

  if (!document) {
    return null;
  }

  return (
    <BdsModal
      ref={modalRef}
      open={isOpen}
      size="dynamic"
      closeButton={!isLoading}
      outzoneClose={!isLoading}
      enterClose={false}
    >
      <ModalContent direction="column" gap="3">
        <ModalHeader title={translate.title} subtitle={translate.subtitle} />

        <Table $isLoading={isLoading}>
          <BdsTableHeader>
            <BdsTableRow>
              <BdsTableTh>
                <BdsTypo variant="fs-14" bold="bold">
                  {translate.tableHeaders.document}
                </BdsTypo>
              </BdsTableTh>
              <BdsTableTh>
                <BdsTypo variant="fs-14" bold="bold">
                  {translate.tableHeaders.lastUpdate}
                </BdsTypo>
              </BdsTableTh>
              <BdsTableTh></BdsTableTh>
            </BdsTableRow>
          </BdsTableHeader>
          <BdsTableBody>
            <RadioGroup>{versions.map(renderVersionRow)}</RadioGroup>
          </BdsTableBody>
        </Table>

        <ModalFooter
          cancelText={translate.cancelButton}
          saveText={translate.saveButton}
          isLoading={isLoading}
          isFormValid={isFormValid}
          onCancel={handleCancel}
          onSave={handleRestore}
        />
      </ModalContent>
    </BdsModal>
  );
};

export default RestoreFileModal;
